import { APP_INITIALIZER, NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CaasContentService } from '../services/caas-content.service';
import { CaasRoutes } from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { NavigationService } from '../services/navigation.service';
import { HomePageComponent } from './home/home-page.component';
import { ErrorPageComponent } from './error/error-page.component';
import { StandardPageComponent } from './standard/standard-page.component';
import { RouteDataGuard } from '../guards/route-data.guard';

function orderRoutes(routes: Routes): Routes {
  const redirectToRoute = routes.find((route) => route.redirectTo);
  if (redirectToRoute) {
    routes.splice(routes.indexOf(redirectToRoute), 1);
    routes.push(redirectToRoute);
  }
  return routes;
}

function simplifyPath(path: string): string {
  const segments = path.split('/');
  const result = [segments[0]];
  for (let i = 1; i < segments.length; i++) {
    if (segments[i] !== segments[i - 1]) {
      result.push(segments[i]);
    }
  }
  return result.join('/');
}

function addSpecialRoutes(
  specialRoutes: CaasRoutes[],
  routeArray: Routes,
  simplifyPath: (path: string) => string,
  type: 'home' | '404' | '500'
): void {
  specialRoutes.forEach((specialRoute) => {
    const simplifiedPath = simplifyPath(
      specialRoute.seoRoute.toLowerCase().replace(/^\/|\/$/g, '')
    );

    if (type === 'home') {
      routeArray.push({
        path: '',
        redirectTo: simplifiedPath,
        pathMatch: 'full',
      });
      routeArray.push({
        path: simplifiedPath,
        component: HomePageComponent,
        data: { pageIdentifier: specialRoute.identifier },
        canActivate: [RouteDataGuard],
      });
    } else if (type === '404') {
      // Separate route for 404
      routeArray.push({
        path: '404',
        component: ErrorPageComponent,
        data: { pageIdentifier: specialRoute.identifier, errorCode: '404' },
        canActivate: [RouteDataGuard],
      });
    } else if (type === '500') {
      // Separate route for 500
      routeArray.push({
        path: '500',
        component: ErrorPageComponent,
        data: { pageIdentifier: specialRoute.identifier, errorCode: '500' },
        canActivate: [RouteDataGuard],
      });
    }
  });
}

const addMetaRoutes = (
  metaRoutes: CaasRoutes[],
  caasRoutes: Routes,
  simplifyPath: (path: string) => string
) => {
  metaRoutes.forEach((metaRoute) => {
    // Check if the metaRoute has children and treat them as root-level routes
    if (metaRoute.children && metaRoute.children.length > 0) {
      metaRoute.children.forEach((childRoute) => {
        // Remove the first segment of the seoRoute path and the trailing slash dynamically
        const childSimplifiedPath = simplifyPath(
          childRoute.seoRoute
            .toLowerCase()
            .replace(/^\/[^/]+\//, '')
            .replace(/\/$/, '')
        );

        // Add the child route as a root-level route
        caasRoutes.push({
          path: childSimplifiedPath,
          component: StandardPageComponent,
          data: { pageIdentifier: childRoute.identifier },
          canActivate: [RouteDataGuard],
        });
      });
    }
  });
};

const addFooterRoutes = (
  footerRoutes: CaasRoutes[],
  caasRoutes: Routes,
  simplifyPath: (path: string) => string
) => {
  footerRoutes.forEach((footerRoute) => {
    // Check if the footerRoute has children and treat them as root-level routes
    if (footerRoute.children && footerRoute.children.length > 0) {
      footerRoute.children.forEach((childRoute) => {
        // Remove the first segment of the seoRoute path (e.g., 'footer/') and the trailing slash dynamically
        const childSimplifiedPath = simplifyPath(
          childRoute.seoRoute
            .toLowerCase()
            .replace(/^\/[^/]+\//, '')
            .replace(/\/$/, '')
        );

        // Add the child route as a root-level route
        caasRoutes.push({
          path: childSimplifiedPath,
          component: StandardPageComponent,
          data: { pageIdentifier: childRoute.identifier },
          canActivate: [RouteDataGuard],
        });
      });
    }
  });
};

function createRoutesRecursively(
  navigation: CaasRoutes[],
  caasRoutes: Routes,
  simplifyPath: (path: string) => string
) {
  if (!navigation || !Array.isArray(navigation)) return;

  navigation.forEach((navItem) => {
    const simplifiedPath = simplifyPath(
      navItem.seoRoute.toLowerCase().replace(/^\/|\/$/g, '')
    );
    caasRoutes.push({
      path: simplifiedPath,
      component: StandardPageComponent,
      data: { pageIdentifier: navItem.identifier },
      canActivate: [RouteDataGuard],
    });

    if (navItem.children && navItem.children.length > 0) {
      caasRoutes.push({ path: simplifiedPath, children: [] });
      createRoutesRecursively(navItem.children, caasRoutes, simplifyPath);
    }
  });
}

export function initializeRoutes(
  NavigationService: NavigationService,
  CaasContentService: CaasContentService,
  router: Router
) {
  return async () => {
    try {
      const navigationRoutes = await firstValueFrom(
        NavigationService.getNonSpecialRoutes()
      );
      const homePage = await firstValueFrom(NavigationService.getHomePage());
      const error404Routes = await firstValueFrom(NavigationService.get404());
      const error500Routes = await firstValueFrom(NavigationService.get500());
      const footerRoutes = await firstValueFrom(
        NavigationService.getFooterRoutes()
      );
      const metaRoutes = await firstValueFrom(
        NavigationService.getMetaRoutes()
      );

      if (!navigationRoutes) {
        console.error('Missing essential navigation data.');
        return;
      }

      const caasRoutes: Routes = [];

      // Handle special routes (Home, 404, 500)
      if (homePage && homePage.length > 0) {
        addSpecialRoutes(homePage, caasRoutes, simplifyPath, 'home');
      }

      if (error500Routes && error500Routes.length > 0) {
        addSpecialRoutes(error500Routes, caasRoutes, simplifyPath, '500');
      }

      if (error404Routes && error404Routes.length > 0) {
        addSpecialRoutes(error404Routes, caasRoutes, simplifyPath, '404');
      }

      if (footerRoutes && footerRoutes.length > 0) {
        addFooterRoutes(footerRoutes, caasRoutes, simplifyPath);
      }

      if (metaRoutes && metaRoutes.length > 0) {
        addMetaRoutes(metaRoutes, caasRoutes, simplifyPath);
      }

      // Create routes for standard navigation
      if (navigationRoutes && navigationRoutes.length > 0) {
        createRoutesRecursively(
          navigationRoutes as CaasRoutes[],
          caasRoutes,
          simplifyPath
        );
      }
      if (!navigationRoutes || navigationRoutes.length === 0) {
        console.error('Navigation routes fehlen oder sind leer.');
        return;
      }

      if (!homePage || homePage.length === 0) {
        console.error('Startseiten-Daten fehlen.');
        return;
      }
      // Determine a simplified home path from the first home page entry
      const homePath = simplifyPath(
        homePage[0].seoRoute.toLowerCase().replace(/^\/|\/$/g, '')
      );
      // Add the special route to handle "index"
      const routes: Routes = [
        { path: '', redirectTo: homePath, pathMatch: 'full' },
        { path: 'startseite', redirectTo: homePath, pathMatch: 'full' },
        { path: 'index', redirectTo: homePath, pathMatch: 'full' },
        ...caasRoutes,
        {
          path: '**',
          component: ErrorPageComponent,
          data: { pageIdentifier: error404Routes[0]?.identifier || '404' },
        },
      ];

      router.resetConfig(orderRoutes(routes));
      // console.log('Routes', routes);
    } catch (error) {
      console.error('Error fetching navigation routes:', error);
    }
  };
}

@NgModule({
  imports: [
    RouterModule.forRoot([], {
      useHash: false,
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      scrollOffset: [0, 56], //inpageNavigation height
    }),
  ],
  exports: [RouterModule],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeRoutes,
      deps: [NavigationService, CaasContentService, Router],
      multi: true,
    },
  ],
})
export class AppRoutingModule {}
